import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { useFocusEffect } from 'expo-router';
import { ChevronRight, Sparkles, Target } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { GoalRow } from '@/db/types';
import { listGoals } from '@/lib/goals';
import { getActiveProperty } from '@/lib/tasks';

/**
 * Compact widget on the home tab — surfaces the 1-3 most active goals so
 * dreams don't get forgotten in their own tab. Picks "in-progress" first,
 * then "planning"; hides if there's nothing in either bucket so the home
 * stays calm for someone who hasn't started a goal yet.
 */
export function GoalsPeek() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [goals, setGoals] = useState<GoalRow[]>([]);

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    if (!prop) return;
    const all = await listGoals(db, prop.id);
    const active = all.filter((g) => g.status === 'in-progress' || g.status === 'planning');
    setGoals(active.slice(0, 3));
  }, [db]);

  useFocusEffect(useCallback(() => {
    fetch();
  }, [fetch]));

  if (goals.length === 0) return null;

  return (
    <View style={styles.wrap}>
      <View style={styles.head}>
        <View style={styles.titleRow}>
          <Sparkles size={14} color={Palette.honeyDeep} strokeWidth={2} />
          <Text style={styles.title}>What you&apos;re building toward</Text>
        </View>
        <Pressable onPress={() => router.push('/projects' as never)} hitSlop={6}>
          <Text style={styles.allLink}>All projects ›</Text>
        </Pressable>
      </View>
      <View style={styles.list}>
        {goals.map((g) => (
          <Pressable
            key={g.id}
            onPress={() => router.push(`/projects/${g.id}` as never)}
            style={({ pressed }) => [styles.row, pressed && { opacity: 0.85 }]}>
            <View style={[styles.dot, g.status === 'in-progress' && styles.dotActive]} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.goalTitle} numberOfLines={1}>{g.title}</Text>
              <Text style={styles.goalMeta} numberOfLines={1}>
                {g.status === 'in-progress' ? 'In progress' : 'Planning'}
                {g.target_date
                  ? ` · ${new Date(g.target_date).toLocaleDateString(undefined, {
                      month: 'short',
                      year: 'numeric',
                    })}`
                  : ''}
                {g.est_cost_cents
                  ? ` · $${(g.est_cost_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                  : ''}
              </Text>
            </View>
            {g.status === 'in-progress' ? (
              <Target size={14} color={Palette.honeyDeep} strokeWidth={2} />
            ) : null}
            <ChevronRight size={14} color={Palette.inkFaint} strokeWidth={1.7} />
          </Pressable>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  title: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.inkSoft,
    letterSpacing: 0.3,
  },
  allLink: {
    fontFamily: Fonts.sansBold,
    fontSize: 12.5,
    color: Palette.forest,
  },
  list: { gap: 2 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
  },
  dot: {
    width: 8, height: 8, borderRadius: 4,
    backgroundColor: Palette.sandDeep,
  },
  dotActive: { backgroundColor: Palette.honey },
  goalTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
  },
  goalMeta: {
    marginTop: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
  },
});
